import { z } from "zod";
import { localizedTextSchema } from "./intake-template";

// TES-389 — authorable "Dagens tips" (DT): DAY-GRANULAR home copy. The
// content model runs a fixed sequence of daily tips (DT-01..DT-60 today) from
// behandlingsstart, each owning the §1 greeting + §5 "För dig idag" for its
// day. This deliberately does NOT reuse WeeklyTheme: that entity is one row
// per DERIVED treatment phase (its phaseKey validates against the 8-phase
// enum), while a DT is keyed by DAY INDEX since the treatment started and
// spans phases. /home-summary resolves, in order:
//
//   published DailyTip for today's dayIndex
//     → published WeeklyTheme for the derived phase
//       → the hardcoded THEMES table
//
// with the TES-372 package overlays (safety pause > replace > contextualize)
// applied ON TOP regardless of source — a CP still only takes over the theme
// when an event changes the patient's context/phase/safety/understanding.

export const DAILY_TIP_STATUSES = ["draft", "published"] as const;
export const dailyTipStatusSchema = z.enum(DAILY_TIP_STATUSES);
export type DailyTipStatus = z.infer<typeof dailyTipStatusSchema>;

// Both locales required + non-empty (same rule as weeklyThemeUpsertSchema): a
// published tip must never render blank in either language.
const nonEmptyLocalizedSchema = z.object({
  sv: z.string().min(1),
  en: z.string().min(1),
});

// Day 1 = the patient's first day on treatment (dayIndex = days since first
// activation + 1). The cap is generous — today's content ships 60 days, but
// the sequence may grow; it exists only to catch fat-fingered indexes.
export const dailyTipDayIndexSchema = z.number().int().min(1).max(730);

// TES-1496 (TES-1458 §4, Emelie 2026-10-20): "DT kan ha upp till två
// artikelknappar när det är relevant." Each button names its own article and
// carries its own label, so two buttons never both read "Läs mer". The same
// article may be linked from several days, but not twice on one day.
export const DAILY_TIP_MAX_LINKS = 2;
export const dailyTipLinkSchema = z.object({
  label: nonEmptyLocalizedSchema,
  articleKey: z
    .string()
    .min(1)
    .max(200)
    .regex(/^[A-Za-z0-9._-]+$/, "articleKey must be an article key"),
});
export type DailyTipLink = z.infer<typeof dailyTipLinkSchema>;

export const dailyTipLinksSchema = z
  .array(dailyTipLinkSchema)
  .max(DAILY_TIP_MAX_LINKS)
  .superRefine((links, ctx) => {
    const keys = new Set<string>();
    const labels = { sv: new Set<string>(), en: new Set<string>() };
    links.forEach((link, i) => {
      if (keys.has(link.articleKey)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: [i, "articleKey"],
          message: "a day may link an article only once",
        });
      }
      keys.add(link.articleKey);
      for (const locale of ["sv", "en"] as const) {
        const label = link.label[locale].trim().toLowerCase();
        if (labels[locale].has(label)) {
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: [i, "label", locale],
            message: "two buttons on one day need different labels",
          });
        }
        labels[locale].add(label);
      }
    });
  });

/** The deep link a DT article button opens. */
export function dailyTipLinkPath(articleKey: string): string {
  return `carebyella://article/${articleKey}`;
}

// Create/update payload (admin + bulk import). `dayIndex` is the identity —
// one row per (tenant, dayIndex). A payload sets either `links` (TES-1496) or
// the single legacy `ctaLabel` + `ctaPath`, never both: with `links` the
// server stores ctaLabel/ctaPath as the first link, so a build that reads
// only ctaPath still opens the first article.
export const dailyTipUpsertSchema = z
  .object({
    dayIndex: dailyTipDayIndexSchema,
    // §1 greeting theme copy for the day.
    title: nonEmptyLocalizedSchema,
    body: nonEmptyLocalizedSchema,
    // §5 "För dig idag" support copy on the same day's theme.
    forYouTitle: nonEmptyLocalizedSchema,
    forYouBody: nonEmptyLocalizedSchema,
    // §5 CTA: either both label + path or neither (mirrors WeeklyTheme).
    ctaLabel: nonEmptyLocalizedSchema.nullable().optional(),
    ctaPath: z.string().min(1).max(200).nullable().optional(),
    links: dailyTipLinksSchema.nullable().optional(),
    status: dailyTipStatusSchema.default("draft"),
  })
  .superRefine((v, ctx) => {
    if (v.links != null && (v.ctaLabel != null || v.ctaPath != null)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["links"],
        message: "set links or ctaLabel/ctaPath, not both",
      });
    }
    const hasLabel = v.ctaLabel != null;
    const hasPath = v.ctaPath != null;
    if (hasLabel !== hasPath) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: [hasLabel ? "ctaPath" : "ctaLabel"],
        message: "ctaLabel and ctaPath must be set together",
      });
    }
  });
export type DailyTipUpsert = z.infer<typeof dailyTipUpsertSchema>;

// Admin read DTO — the full stored tip for one day.
export const dailyTipDtoSchema = z.object({
  id: z.string().uuid(),
  dayIndex: dailyTipDayIndexSchema,
  title: localizedTextSchema,
  body: localizedTextSchema,
  forYouTitle: localizedTextSchema,
  forYouBody: localizedTextSchema,
  ctaLabel: localizedTextSchema.nullable(),
  ctaPath: z.string().nullable(),
  // TES-1496 — null on a tip authored with the single legacy CTA.
  links: z.array(dailyTipLinkSchema).max(DAILY_TIP_MAX_LINKS).nullable(),
  status: dailyTipStatusSchema,
});
export type DailyTipDto = z.infer<typeof dailyTipDtoSchema>;

// TES-409 — the patient-facing "För dig idag"-history: past days' card copy
// (the §5 slot: forYouTitle/forYouBody), locale-resolved server-side, capped
// at the patient's CURRENT treatment day so future content never leaks. The
// long read is joined client-side via `articleKey` (parsed from the tip's
// carebyella://article/<key> ctaPath; null when the tip has no read).
// TES-1496 — `articleKeys` lists every read the day links (its `links`, or
// the legacy ctaPath), in button order; `articleKey` stays the first. Sent
// only to a build at or after CLIENT_CONTRACT_CHANGES.dailyTipLinks.
export const dailyTipHistoryEntrySchema = z.object({
  dayIndex: dailyTipDayIndexSchema,
  title: z.string(),
  body: z.string(),
  articleKey: z.string().nullable(),
  articleKeys: z.array(z.string()).optional(),
});
export type DailyTipHistoryEntry = z.infer<typeof dailyTipHistoryEntrySchema>;

export const dailyTipHistoryResponseSchema = z.object({
  entries: z.array(dailyTipHistoryEntrySchema),
});
export type DailyTipHistoryResponse = z.infer<
  typeof dailyTipHistoryResponseSchema
>;
